import type { DataTablePagination } from '#/shared/ui/data-table'
import { useTableSearchParams } from './search-params'

export interface UseTablePaginationResult {
  /** Absolute window bounds the user asked for, already read from the URL. */
  from: number
  to: number
  /** "Show per page" — how the bottom bar splits the window. */
  size: number
  /** Current sub-page within the window (clamped to the data). */
  page: number
  /** Ready-to-spread into `<DataTable pagination={...} />`. */
  pagination: DataTablePagination
  /**
   * Values the DataDropdown renders as `{from}-{to} / {total}`. These mirror
   * `from`/`to` while data exists and collapse to 0 when it doesn't, so an
   * empty table shows `0-0 / 0` instead of the previous range.
   */
  rangeFrom: number
  rangeTo: number
  rangeDisabled: boolean
  onRangeChange: (from: number, to: number) => void
}

/**
 * Two levels of pagination compose here:
 *
 * - The **window** (`from`/`to`, driven by the toolbar DataDropdown and the
 *   bottom bar's right-side pill) is the slice of the dataset the table pages
 *   through. It can be any size — only the visible sub-page is ever fetched.
 * - The **bottom bar** splits that window by the "show per page" size, so a
 *   100-row window with size 25 paginates as 4 sub-pages of 25.
 *
 * Pages read `from`/`to`/`size`/`page` (via this hook or `useTableSearchParams`)
 * to fetch just the visible sub-page (`visibleSubRange` + `planWindowFetch`),
 * spread the returned `pagination` into `DataTable`, and wire
 * `rangeFrom`/`rangeTo`/`onRangeChange` into the toolbar.
 */
export function useTablePagination(
  totalItems: number,
): UseTablePaginationResult {
  const { from, to, size, page, setRange, setSize, setPage } =
    useTableSearchParams()

  const hasData = totalItems > 0
  // The window can be typed past the end of the dataset; clamp the display to
  // the rows that actually exist.
  const windowEnd = Math.min(to, totalItems)
  const windowItems = hasData ? Math.max(0, windowEnd - from + 1) : 0

  const totalPages = windowItems > 0 ? Math.ceil(windowItems / size) : 0
  // The URL page can outrun a freshly shrunk window (smaller tab, bigger size);
  // clamp so the bottom bar never points past the last sub-page.
  const safePage = Math.min(Math.max(1, page), Math.max(1, totalPages))

  // The window steps by its own span — the right pill and the DataDropdown both
  // drive this, paging the whole window (1-100 -> 101-200). `to` is clamped to
  // the dataset total so a step never reaches past the last row.
  const span = to - from + 1
  const hasPrevWindow = from > 1
  const hasNextWindow = from + span <= totalItems

  return {
    from,
    to,
    size,
    page: safePage,
    pagination: {
      page: safePage,
      pageSize: size,
      totalItems: windowItems,
      totalPages,
      onPageChange: setPage,
      onPageSizeChange: setSize,
      window: {
        from: hasData ? from : 0,
        to: hasData ? windowEnd : 0,
        total: totalItems,
        hasPrev: hasPrevWindow,
        hasNext: hasNextWindow,
        onPrev: hasPrevWindow
          ? () => setRange(Math.max(1, from - span), from - 1)
          : undefined,
        onNext: hasNextWindow
          ? () =>
              setRange(from + span, Math.min(totalItems, from + 2 * span - 1))
          : undefined,
      },
    },
    rangeFrom: hasData ? from : 0,
    rangeTo: hasData ? windowEnd : 0,
    rangeDisabled: !hasData,
    onRangeChange: setRange,
  }
}
